import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Chip, Skeleton } from '@nextui-org/react';
import { Trophy, CalendarDays, RefreshCw } from 'lucide-react';
import proService, { ProEvent, ProLiveGame } from '../services/proService';
import DaySelector, { DAY_RANGE, buildDays } from '../components/spiele/DaySelector';
import LiveGamesSection from '../components/spiele/LiveGamesSection';
import TournamentCard from '../components/spiele/TournamentCard';

const ACCENT = '#3fced1';
const REFRESH_INTERVAL_MS = 30_000;

const SpielePage: React.FC = () => {
  const days = useMemo(buildDays, []);
  const [selectedDayIndex, setSelectedDayIndex] = useState(DAY_RANGE); // Heute
  const [events, setEvents] = useState<ProEvent[]>([]);
  const [liveGames, setLiveGames] = useState<ProLiveGame[]>([]);
  const [loading, setLoading] = useState(true);
  // Collapsed-Set statt Expanded-Set: neue Turniere sind per Default offen
  // und ein Silent-Refresh überschreibt den User-Zustand nicht.
  const [collapsedTournaments, setCollapsedTournaments] = useState<Set<string>>(new Set());
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);
  const [silentRefreshing, setSilentRefreshing] = useState(false);
  const [, setNowTick] = useState(0); // Ticker für "vor Xs"-Anzeige

  const currentDay = days[selectedDayIndex];
  const isToday = currentDay.offset === 0;

  const loadLiveGames = useCallback(async () => {
    try {
      const live = await proService.getLiveGamesCache();
      setLiveGames(live.filter((g) => proService.isLive(g)));
    } catch (err) {
      console.error('Fehler beim Laden der Live-Games:', err);
      setLiveGames([]);
    }
  }, []);

  const loadEvents = useCallback(async (offset: number, showLoading: boolean = true) => {
    if (showLoading) setLoading(true);
    try {
      const raw = await proService.getEventsByDateWithStats(offset);
      const filtered = proService.filterAndSortByDay(raw, offset);
      setEvents(filtered);
      // Bei Tag-Wechsel / Initial-Load: alle Gruppen wieder öffnen
      if (showLoading) setCollapsedTournaments(new Set());
    } catch (err) {
      console.error('Fehler beim Laden der Events:', err);
      setEvents([]);
    } finally {
      if (showLoading) setLoading(false);
      setLastRefreshAt(Date.now());
    }
  }, []);

  // Initial + bei Day-Wechsel laden
  useEffect(() => {
    loadEvents(currentDay.offset, true);
  }, [currentDay.offset, loadEvents]);

  // Live-Games bei Bedarf laden + Auto-Refresh (silent, alle 30s)
  useEffect(() => {
    if (!isToday) {
      setLiveGames([]);
      return;
    }
    loadLiveGames();
    const interval = setInterval(() => {
      setSilentRefreshing(true);
      Promise.all([loadLiveGames(), loadEvents(currentDay.offset, false)]).finally(() =>
        setSilentRefreshing(false)
      );
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isToday, currentDay.offset, loadLiveGames, loadEvents]);

  // Sekunden-Ticker für den "Zuletzt aktualisiert"-Indikator
  useEffect(() => {
    if (lastRefreshAt === null) return;
    const ticker = setInterval(() => setNowTick((t) => t + 1), 1000);
    return () => clearInterval(ticker);
  }, [lastRefreshAt]);

  const toggleTournament = (name: string) => {
    setCollapsedTournaments((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const groupedEvents = useMemo(() => proService.groupByTournament(events), [events]);

  const goToDay = (index: number) => {
    if (index < 0 || index >= days.length) return;
    setSelectedDayIndex(index);
  };

  const secondsAgo =
    lastRefreshAt !== null ? Math.max(0, Math.floor((Date.now() - lastRefreshAt) / 1000)) : null;

  const fullDate = currentDay.date.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  return (
    <>
      <Helmet>
        <title>Spiele – Live & Ergebnisse | DartCloud</title>
        <meta
          name="description"
          content="Alle Dart-Spiele im Überblick: Live-Spiele, heutige Ergebnisse und die kommenden sieben Tage. PDC, Premier League, European Tour und mehr."
        />
      </Helmet>

      <div className="min-h-screen bg-zinc-950 text-white">
        {/* ============ Hero ============ */}
        <section className="relative overflow-hidden border-b border-zinc-800/80">
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-950" />
          <div
            className="absolute -top-28 left-1/3 w-[520px] h-[260px] rounded-full blur-3xl opacity-15 pointer-events-none"
            style={{ backgroundColor: ACCENT }}
          />
          <div
            className="absolute -bottom-32 -right-20 w-[340px] h-[340px] rounded-full blur-3xl opacity-10 pointer-events-none"
            style={{ backgroundColor: ACCENT }}
          />

          <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-12 flex items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5 min-w-0">
              <div
                className="p-2.5 rounded-xl border flex-shrink-0"
                style={{ backgroundColor: `${ACCENT}14`, borderColor: `${ACCENT}33` }}
              >
                <Trophy size={22} color={ACCENT} />
              </div>
              <div className="min-w-0">
                <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
                  Spiele &amp; Ergebnisse
                </h1>
                <p className="text-sm text-gray-400 mt-0.5">
                  Live-Spiele, Ergebnisse und kommende Matches – PDC, Premier League, European
                  Tour &amp; mehr.
                </p>
              </div>
            </div>

            {/* Zuletzt-aktualisiert-Indikator (nur Heute, da nur dort Auto-Refresh läuft) */}
            {isToday && secondsAgo !== null && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-zinc-900/80 border border-zinc-800 text-xs text-gray-400 flex-shrink-0">
                <RefreshCw
                  size={12}
                  className={silentRefreshing ? 'animate-spin' : 'text-gray-500'}
                  style={silentRefreshing ? { color: ACCENT } : undefined}
                />
                <span className="tabular-nums">
                  {secondsAgo <= 1 ? 'Gerade aktualisiert' : `Aktualisiert vor ${secondsAgo}s`}
                </span>
              </div>
            )}
          </div>
        </section>

        {/* ============ Sticky Day-Selector ============ */}
        <DaySelector days={days} selectedIndex={selectedDayIndex} onSelect={goToDay} />

        {/* ============ Content ============ */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10 space-y-8 md:space-y-12">
          {/* Live-Sektion prominent ganz oben (nur Heute, nur wenn Spiele live sind) */}
          {isToday && <LiveGamesSection games={liveGames} />}

          {/* Tages-Sektion */}
          <section aria-label={`Spiele ${currentDay.label}`}>
            <div className="flex items-center gap-3 mb-6">
              <div
                className="p-2.5 rounded-xl border flex-shrink-0"
                style={{ backgroundColor: `${ACCENT}14`, borderColor: `${ACCENT}33` }}
              >
                <CalendarDays size={20} color={ACCENT} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-xl font-bold text-white truncate">
                  {currentDay.label}
                </h2>
                <p className="text-xs md:text-sm text-gray-500 truncate">{fullDate}</p>
              </div>
              {!loading && (
                <Chip
                  size="sm"
                  variant="flat"
                  classNames={{
                    base: 'bg-zinc-900 border border-zinc-800 flex-shrink-0',
                    content: 'text-xs text-gray-300',
                  }}
                >
                  {events.length} {events.length === 1 ? 'Spiel' : 'Spiele'}
                </Chip>
              )}
            </div>

            {loading ? (
              <EventsSkeleton />
            ) : events.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="space-y-8 md:space-y-10">
                {Array.from(groupedEvents.entries()).map(([tournamentName, tEvents]) => (
                  <TournamentCard
                    key={tournamentName}
                    name={tournamentName}
                    events={tEvents}
                    expanded={!collapsedTournaments.has(tournamentName)}
                    onToggle={() => toggleTournament(tournamentName)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

// ============================================
// Empty-State & Skeleton
// ============================================

const EmptyState: React.FC = () => (
  <div className="rounded-2xl border border-dashed border-zinc-800 py-16 text-center">
    <div
      className="inline-flex p-3.5 rounded-2xl mb-4"
      style={{ backgroundColor: `${ACCENT}14` }}
    >
      <Trophy size={28} color={ACCENT} className="opacity-80" />
    </div>
    <p className="text-lg font-semibold text-white">Keine Spiele an diesem Tag</p>
    <p className="text-sm text-gray-500 mt-1">
      Wähle oben einen anderen Tag aus der Navigation.
    </p>
  </div>
);

/** Skeleton: 2 Gruppen-Cards mit je 3 Match-Reihen (statt Spinner) */
const EventsSkeleton: React.FC = () => (
  <div className="space-y-4 md:space-y-5">
    {Array.from({ length: 2 }).map((_, g) => (
      <div
        key={g}
        className="rounded-2xl border border-zinc-800 bg-zinc-900/60 overflow-hidden"
      >
        {/* Gruppen-Header */}
        <div className="flex items-center gap-3 px-4 md:px-5 py-3.5">
          <Skeleton className="w-8 h-8 rounded-lg bg-zinc-800 flex-shrink-0" />
          <Skeleton className="h-4 w-48 rounded-md bg-zinc-800" />
          <div className="flex-1" />
          <Skeleton className="h-4 w-14 rounded-full bg-zinc-800" />
        </div>
        {/* Match-Reihen */}
        <div className="border-t border-zinc-800 divide-y divide-zinc-800/70">
          {Array.from({ length: 3 }).map((_, r) => (
            <div key={r} className="flex items-center gap-4 px-4 md:px-5 py-4">
              <Skeleton className="hidden md:block h-3 w-16 rounded-md bg-zinc-800 flex-shrink-0" />
              <div className="flex-1 flex items-center justify-end gap-2.5 min-w-0">
                <Skeleton className="h-3.5 w-24 md:w-28 rounded-md bg-zinc-800" />
                <Skeleton className="w-7 h-7 rounded-full bg-zinc-800 flex-shrink-0" />
              </div>
              <Skeleton className="h-6 w-14 rounded-md bg-zinc-800 flex-shrink-0" />
              <div className="flex-1 flex items-center gap-2.5 min-w-0">
                <Skeleton className="w-7 h-7 rounded-full bg-zinc-800 flex-shrink-0" />
                <Skeleton className="h-3.5 w-24 md:w-28 rounded-md bg-zinc-800" />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default SpielePage;
